import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form'
import {
    DEFAULT_ATTENDANT_PERMISSIONS,
    Permission,
    SIDEBAR_MODULE_LABELS,
    SIDEBAR_MODULE_PERMISSIONS,
    SidebarModule,
} from '@/config/permissions'
import { teamPermissionsConfigQuery } from '@/queries/team.query'
import { TeamService } from '@/services/team.service'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo } from 'react'
import { type Control, useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

const permissionsFormSchema = z.object({
  modules: z.array(z.nativeEnum(SidebarModule)),
})

type PermissionsFormValues = z.infer<typeof permissionsFormSchema>

const ALL_MODULES = Object.values(SidebarModule)

function mapPermissionsToModules(permissions: Permission[]) {
  return ALL_MODULES.filter((module) => {
    const requiredPermissions = SIDEBAR_MODULE_PERMISSIONS[module]
    if (requiredPermissions.length === 0) {
      return true
    }

    return requiredPermissions.some((permission) =>
      permissions.includes(permission)
    )
  })
}

function mergeModulePermissions(
  selectedModules: SidebarModule[],
  basePermissions: Permission[]
) {
  const allPermissions = new Set<Permission>()

  selectedModules.forEach((module) => {
    const modulePermissions = SIDEBAR_MODULE_PERMISSIONS[module]
    modulePermissions.forEach((permission) => allPermissions.add(permission))
  })

  basePermissions.forEach((permission) => {
    const isModulePermission = Object.values(SIDEBAR_MODULE_PERMISSIONS).some(
      (permissions) => permissions.includes(permission)
    )

    if (!isModulePermission) {
      allPermissions.add(permission)
    }
  })

  return Array.from(allPermissions)
}

function toggleModuleSelection(
  value: PermissionsFormValues['modules'],
  module: SidebarModule,
  checked: boolean | 'indeterminate'
) {
  if (checked) {
    return [...value, module]
  }

  return value.filter((currentValue) => currentValue !== module)
}

type ModulePermissionFieldProps = {
  readonly control: Control<PermissionsFormValues>
  readonly module: SidebarModule
}

function ModulePermissionField(props: ModulePermissionFieldProps) {
  const { control, module } = props

  return (
    <FormField
      control={control}
      name='modules'
      render={({ field }) => (
        <FormItem className='flex flex-row items-start space-x-3 space-y-0'>
          <FormControl>
            <Checkbox
              checked={field.value?.includes(module)}
              onCheckedChange={(checked) => {
                field.onChange(
                  toggleModuleSelection(field.value, module, checked)
                )
              }}
            />
          </FormControl>
          <FormLabel className='font-normal cursor-pointer'>
            {SIDEBAR_MODULE_LABELS[module]}
          </FormLabel>
        </FormItem>
      )}
    />
  )
}

export function TeamPermissionsForm() {
  const queryClient = useQueryClient()
  const { data: permissionsConfig, isLoading } = useQuery(
    teamPermissionsConfigQuery
  )

  const effectivePermissionsConfig =
    permissionsConfig ?? DEFAULT_ATTENDANT_PERMISSIONS

  const enabledModules = useMemo(
    () => mapPermissionsToModules(effectivePermissionsConfig),
    [effectivePermissionsConfig]
  )

  const form = useForm<PermissionsFormValues>({
    resolver: zodResolver(permissionsFormSchema),
    defaultValues: {
      modules: enabledModules,
    },
  })

  useEffect(() => {
    form.reset({ modules: enabledModules })
  }, [enabledModules, form])

  const mutation = useMutation({
    mutationFn: async (selectedModules: SidebarModule[]) =>
      TeamService.updatePermissions(
        mergeModulePermissions(selectedModules, effectivePermissionsConfig)
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-permissions-config'] })
      queryClient.invalidateQueries({ queryKey: ['team-permissions'] })
      toast.success('Permissões atualizadas', {
        description:
          'As permissões dos atendentes foram atualizadas com sucesso.',
      })
    },
    onError: () => {
      toast.error('Erro ao atualizar permissões', {
        description:
          'Ocorreu um erro ao atualizar as permissões. Tente novamente.',
      })
    },
  })

  function onSubmit(data: PermissionsFormValues) {
    mutation.mutate(data.modules)
  }

  if (isLoading) {
    return <div>Carregando...</div>
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-8'>
        <div>
          <h3 className='mb-4 text-lg font-medium'>
            Permissões dos Atendentes
          </h3>
          <FormDescription className='mb-6'>
            Selecione quais módulos da sidebar os Atendentes podem visualizar.
            A configuração também define os acessos efetivos usados pelo app
            para os atendentes.
          </FormDescription>
          <FormField
            control={form.control}
            name='modules'
            render={() => (
              <FormItem>
                <div className='space-y-4'>
                  {ALL_MODULES.map((module) => (
                    <ModulePermissionField
                      key={module}
                      control={form.control}
                      module={module}
                    />
                  ))}
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <Button type='submit' disabled={mutation.isPending}>
          {mutation.isPending ? 'Salvando...' : 'Salvar Permissões'}
        </Button>
      </form>
    </Form>
  )
}
